import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getDocument, GlobalWorkerOptions, version } from 'pdfjs-dist';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './PdfViewer.css';

// Configure worker
GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ url }) => {
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const timerRef = useRef(null);

  // Initialize loading the PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const loadingTask = getDocument(url);
        const loadedPdf = await loadingTask.promise;
        
        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading PDF:", err);
        setError("Failed to load PDF document.");
        setIsLoading(false);
      }
    };

    if (url) {
      loadPdf();
    }
  }, [url]);

  // Handle resizing
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    // Initial size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Render page
  const renderPage = useCallback(async () => {
    if (!pdf || !canvasRef.current || !containerWidth) return;

    try {
      const page = await pdf.getPage(currentPage);
      
      // Calculate scale to fit width
      // Use a base scale of 1 first to get dimensions
      const viewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;

      // Cancel previous render if any
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      renderTaskRef.current = page.render(renderContext);
      await renderTaskRef.current.promise;
    } catch (err) {
      if (err.name !== 'RenderingCancelledException') {
        console.error("Error rendering page:", err);
      }
    }
  }, [pdf, currentPage, containerWidth]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Auto-advance logic
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    timerRef.current = setInterval(() => {
      setCurrentPage(prev => {
        if (prev >= numPages) return 1;
        return prev + 1;
      });
    }, 30000); // 30 seconds
  }, [numPages]);

  useEffect(() => {
    if (numPages > 0) {
      resetTimer();
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [numPages, resetTimer]);

  // Navigation handlers
  const handlePrev = () => {
    resetTimer();
    setCurrentPage(prev => (prev <= 1 ? numPages : prev - 1));
  };

  const handleNext = () => {
    resetTimer();
    setCurrentPage(prev => (prev >= numPages ? 1 : prev + 1));
  };

  return (
    <div className="pdf-viewer-container" ref={containerRef}>
      {error && <div className="pdf-error">{error}</div>}
      {isLoading && <div className="pdf-loading">Loading PDF...</div>}
      
      <div className="pdf-canvas-wrapper">
        <canvas ref={canvasRef} />
      </div>

      <div className="pdf-controls">
        <button className="pdf-nav-btn" onClick={handlePrev} disabled={isLoading || currentPage <= 1} aria-label="Previous Page">
          <FaChevronLeft />
        </button>
        
        <span className="pdf-page-info">
          {currentPage} / {numPages}
        </span>

        <button className="pdf-nav-btn" onClick={handleNext} disabled={isLoading || currentPage >= numPages} aria-label="Next Page">
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default PdfViewer;
